import { create } from 'zustand';

/**
 * Chain-orchestrator progress stage for a video generation row (W1 subject
 * engine, spec §5.1). Mirrors the backend's `chain_meta.stage` /
 * `sendInferenceUpdate` `stage` field.
 */
export type GenerationStage = 'subject' | 'animating';

interface GenerationStageStore {
  stages: Record<number, GenerationStage>;
  setStage: (id: number, stage: GenerationStage) => void;
  clearStage: (id: number) => void;
}

// Forward-only ranking — a late/duplicate 'subject' WS push must never
// regress an id that has already advanced to 'animating'.
const STAGE_RANK: Record<GenerationStage, number> = { subject: 0, animating: 1 };

/**
 * LOAD-BEARING: `sanitize-response.ts` strips `chain_meta` from every
 * customer response, so `stage` exists ONLY on WebSocket pushes — it is
 * never present on a `['generations']` query-cache row. `InferenceNotifications`
 * invalidates that query on every processing event, and a refetch would wipe
 * any stage data if it lived merged into the cache. This store is therefore
 * intentionally SEPARATE from React Query — keyed by inference id, written by
 * `InferenceNotifications` on each WS push, read by the playground card per
 * row. It is NOT persisted: a page refresh mid-chain starts empty and shows
 * the generic single-stage label until the next WS push arrives (designed
 * degradation, not a bug).
 */
export const useGenerationStageStore = create<GenerationStageStore>((set) => ({
  stages: {},

  setStage: (id, stage) =>
    set((state) => {
      const current = state.stages[id];
      if (current && STAGE_RANK[current] >= STAGE_RANK[stage]) return state;
      return { stages: { ...state.stages, [id]: stage } };
    }),

  clearStage: (id) =>
    set((state) => {
      if (!(id in state.stages)) return state;
      const next = { ...state.stages };
      delete next[id];
      return { stages: next };
    }),
}));
